import { Col, Collapse, Row, Typography } from "antd";
import type { CollapseProps } from "antd";
import { useState } from "react";
import StatCard from "../components/analytics/StatCard";
import RemoteSelect from "../components/analytics/RemoteSelect";
import DashboardSection, {
  DashboardChart,
  DrillLink,
} from "../components/analytics/DashboardSection";
import LoginsCard from "../components/analytics/LoginsCard";
import AssessmentsCard from "../components/analytics/AssessmentsCard";
import AffiliatesCard from "../components/analytics/AffiliatesCard";
import IncidentsCard from "../components/analytics/IncidentsCard";
import RewardsLoyaltyCard from "../components/analytics/RewardsLoyaltyCard";
import RisksCard from "../components/analytics/RisksCard";
import {
  getCompanies,
  getCompanyAssets,
  getCompanyDescendants,
  getCompanyEmployees,
  getCompanyManagers,
  getDashboardUserActivity,
  getMaterialEngagement,
} from "../repository/data.repository";
import { PaginationInput } from "../repository/data.default";
import { createSearchRequest } from "../hooks/useSearchRequest";
import {
  QueryCondition,
  QueryOperation,
} from "../code/search-query/search-query";
import { dateValue } from "../code/search-query/query-mapper";
import dayjs from "dayjs";

const companySearch = createSearchRequest(getCompanies, "name");

// A Company has no `companyUid` of its own, so the Platform Status scope cannot be applied to the
// company list the way it is applied to users and assets — which is why this card used to ignore the
// scope entirely and sit at "16 of 16" with a company selected. The row looked scoped while one card
// quietly answered a different question.
const companyScopeOf = (conditions: QueryCondition[]) => {
  const companyUid = conditions.find((c) => c.field === "companyUid")?.values[0];
  return companyUid ? String(companyUid) : undefined;
};

// Companies in scope: the whole book when unscoped, otherwise the selected company's WHOLE SUBTREE.
//
// This used to query `parentCompanyUid == selected` and add 1 for the company itself, which counts only
// direct children — a grandchild was silently dropped. With IMEQ selected the card read 3 against a true
// subtree of 4, missing "IMEQ Tankers East Med". The old comment blamed the query pipeline for having no
// OR; that was never the binding constraint, because a subtree is *recursive* and no flat OR expresses it.
// `Descendants` already returned the recursive set (company + every descendant, any depth) — the card
// simply was not asking for it. PD-01 §3.3: the parent-child relationship must be preserved in reports.
const fetchCompaniesInScope = (input: PaginationInput) => {
  const companyUid = companyScopeOf(input.searchQuery.conditions);
  if (!companyUid) return getCompanies(input);
  return getCompanyDescendants(companyUid).then((companies) => ({
    data: [] as never[],
    totalRecords: companies.length,
  }));
};

// New companies this month: the same generic company search, narrowed to accounts created since the
// first of the current month. GreaterThanOrEqual rather than Between on purpose — CreatedOn carries a
// time, so a Between against YYYY-MM-DD bounds silently drops anything created later in the day on the
// closing bound. Nothing is created in the future, so an open upper end is equivalent and safe.
// Only ever rendered unscoped: growth of the client book is a platform figure, and scoping it to a
// single company would mean reporting "is this one company new" as if it were a trend.
const fetchNewCompaniesThisMonth = (input: PaginationInput) =>
  getCompanies({
    ...input,
    searchQuery: {
      ...input.searchQuery,
      conditions: [
        {
          field: "createdOn",
          operation: QueryOperation.GreaterThanOrEqual,
          values: [dateValue(dayjs().startOf("month"))],
        },
      ],
    },
  });

// Registered users come from the server-side dashboard rollup rather than a paginated count; this
// adapter reads the company scope off the standard StatCard conditions so the card stays uniform.
const fetchRegisteredUsers = (input: PaginationInput) => {
  const companyUid = input.searchQuery.conditions.find(
    (c) => c.field === "companyUid",
  )?.values[0];
  return getDashboardUserActivity({
    companyUid: companyUid ? String(companyUid) : undefined,
  }).then((r) => ({ data: [] as never[], totalRecords: r.registeredUsers }));
};

// Admin platform overview. Platform-status counts (with their own company scope) are pinned on top;
// every other metric is grouped into a per-module accordion, each carrying its own filter
// (DashboardSection) so admins scope charts by context one section at a time.
function DashboardPage() {
  const [statCompanyUid, setStatCompanyUid] = useState<string | undefined>();

  const statScope: QueryCondition[] = statCompanyUid
    ? [{ field: "companyUid", operation: QueryOperation.Equal, values: [statCompanyUid] }]
    : [];

  const engagementCharts: DashboardChart[] = [
    {
      title: "Login activity",
      supportsDepartment: true,
      fullWidth: true,
      // No drill-down: the platform has no logins module to land on. PD-01 §21.4 asks for Login Logs
      // under System Logs, which does not exist either — the same gap seen from the other end.
      render: (scope) => <LoginsCard {...scope} />,
    },
    {
      title: "WellBeing material engagement",
      fetch: getMaterialEngagement,
      mode: { kind: "breakdown", groupField: "status", chart: "ring", dateField: "createdOn" },
      drillTo: "wellbeing-corner/engagement",
      drillLabel: "View engagement",
    },
  ];

  const testsCharts: DashboardChart[] = [
    {
      title: "Assessments",
      supportsDepartment: true,
      fullWidth: true,
      drillTo: "tests/answers",
      drillLabel: "View answers",
      render: (scope) => <AssessmentsCard {...scope} />,
    },
  ];

  // Counselling and trainings have no department scoping, so the section offers company + date only.
  const affiliatesCharts: DashboardChart[] = [
    {
      title: "Affiliates",
      fullWidth: true,
      drillTo: "affiliates/counselling-requests",
      drillLabel: "View counselling requests",
      render: (scope) => <AffiliatesCard {...scope} />,
    },
  ];

  const incidentsCharts: DashboardChart[] = [
    {
      title: "Incidents",
      supportsDepartment: true,
      fullWidth: true,
      drillTo: "incidents/all",
      drillLabel: "View incidents",
      render: (scope) => <IncidentsCard {...scope} />,
    },
  ];

  const rewardsCharts: DashboardChart[] = [
    {
      title: "Rewards & Loyalty",
      supportsDepartment: true,
      fullWidth: true,
      drillTo: "rewards/redemptions",
      drillLabel: "View redemptions",
      render: (scope) => <RewardsLoyaltyCard {...scope} />,
    },
  ];

  const moduleItems: CollapseProps["items"] = [
    { key: "engagement", label: "Clientele Engagement", children: <DashboardSection charts={engagementCharts} /> },
    { key: "tests", label: "Assessments & Tests", children: <DashboardSection charts={testsCharts} /> },
    { key: "affiliates", label: "Affiliates", children: <DashboardSection charts={affiliatesCharts} /> },
    { key: "incidents", label: "Incidents", children: <DashboardSection charts={incidentsCharts} /> },
    { key: "rewards", label: "Rewards & Loyalty", children: <DashboardSection charts={rewardsCharts} /> },
    {
      key: "risks",
      label: "Risks",
      // Risks renders its own card rather than going through DashboardSection, so its drill-down
      // hangs off the panel header instead of the chart wrapper.
      extra: <DrillLink to="reports/kpis" label="View KPI results" />,
      children: <RisksCard />,
    },
  ];

  return (
    <Col className="relative flex flex-auto flex-col overflow-hidden overflow-y-auto">
      <Row className="sticky top-0 z-10 flex flex-row flex-wrap items-center bg-white p-4">
        <Typography.Title className="m-0 flex-auto" level={4}>
          Dashboard
        </Typography.Title>
      </Row>

      {/* Natural-height content so the outer column scrolls. */}
      <Col className="flex flex-col gap-4 p-4 pt-0">
        <div>
          <div className="mb-2 flex flex-row flex-wrap items-center gap-3">
            <Typography.Title level={5} type="secondary" className="m-0">
              PLATFORM STATUS
            </Typography.Title>
            <RemoteSelect
              className="w-56"
              placeholder="All companies"
              value={statCompanyUid}
              onChange={setStatCompanyUid}
              request={companySearch}
            />
          </div>
          <Row className="flex flex-row flex-wrap gap-3">
            <StatCard
              title="Companies"
              fetch={fetchCompaniesInScope}
              conditions={statScope}
              subCounts={
                statCompanyUid
                  ? undefined
                  : [{ label: "new this month", fetch: fetchNewCompaniesThisMonth }]
              }
              drillTo="companies/grid"
              drillLabel="View companies"
            />
            <StatCard
              title="Users"
              fetch={fetchRegisteredUsers}
              conditions={statScope}
              subCounts={[
                { label: "managers", fetch: getCompanyManagers },
                { label: "employees", fetch: getCompanyEmployees },
              ]}
              drillTo="users/all"
              drillLabel="View users"
            />
            <StatCard
              title="Assets"
              fetch={getCompanyAssets}
              conditions={statScope}
              drillTo="companies/grid"
              drillLabel="View companies"
            />
          </Row>
        </div>

        <Collapse items={moduleItems} defaultActiveKey={["engagement", "tests", "risks"]} />
      </Col>
    </Col>
  );
}

export default DashboardPage;
